/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useLoading, useShowRegisterModal, usePersonChanged } from '../contexts/GlobalStateContext';
import BattenburgLoader from '../common/BattenburgLoader';
import StationSelector from './StationSelector';
import { fetchWithAuth } from '../services/api';

const RegisterForm = ({ onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newUserId, setNewUserId] = useState(null);
  const [showStationSelector, setShowStationSelector] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('Admin');
  const [homeStation, setHomeStation] = useState('');
  const [stationOptions, setStationOptions] = useState([]);
  const [captionText, setCaptionText] = useState('Registrera ny användare');
  const { loading, setLoading } = useLoading();
  const { setPersonChanged } = usePersonChanged();
  const { setShowRegisterModal } = useShowRegisterModal();

  useEffect(() => {
    fetchWithAuth('/api/stationer/alla')
      .then(res => (res.ok ? res.json() : []))
      .then(setStationOptions)
      .catch(() => setStationOptions([]));
  }, []);

  const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #ccc',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#333',
  }

  const registerUserOnServerAsync = async (username, password, role, homeStation) => {
    try {
      const response = await fetchWithAuth('/api/authorization/register', {
        method: 'POST',
        body: JSON.stringify({ username, password, role, homeStation }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        let message = 'Registrering misslyckades';
        try {
          message = JSON.parse(errorText)?.message || message;
        } catch {
          // svaret var inte JSON (t.ex. tomt 401-svar) - behåll standardmeddelandet
        }
        console.error('Registration error:', errorText);
        return { success: false, message };
      }
      return await response.json();
    } catch (error) {
      console.error('Network error:', error);
      throw error;
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!username.trim()) return setCaptionText('Ange användarnamn');
    if (!password) return setCaptionText('Ange lösenord');
    if (password.length < 8) return setCaptionText('Lösenordet måste vara minst 8 tecken');
    if (password !== confirmPassword) return setCaptionText('Lösenorden matchar inte');
    if (selectedRole === 'Admin' && !homeStation) return setCaptionText('Välj en hemmastation');

    setLoading(true);
    try {
      const response = await registerUserOnServerAsync(username, password, selectedRole, homeStation || undefined);
      if (response?.token) {
        setCaptionText('Registrering lyckades!');
        setNewUserId(response.userId);
        setPersonChanged(prev => !prev);
        setShowStationSelector(true);
      } else {
        setCaptionText(response.message || 'Registrering misslyckades');
      }
    } catch {
      setCaptionText('Registrering misslyckades. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setSelectedRole('Admin');
    setHomeStation('');
    setCaptionText('Registrera ny användare');
    setShowRegisterModal(false);
    onClose()
  };
  const anyDisabling = () => {
    return loading || !username.trim() || !password || !confirmPassword || newUserId ||
      (selectedRole === 'Admin' && !homeStation);
  }

  return (
    <Modal labelledById="register-modal-title" onClose={handleClose} width={showStationSelector ? 'xl' : 'xs'}>
      <div className="tw-flex tw-gap-6 tw-items-start tw-transition-all tw-duration-500">
        {/* Vänster sida: formulär (1/5) */}
        <div className="tw-w-1/5 tw-min-w-[250px]">
          <form onSubmit={handleRegister}>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-text-lg tw-font-semibold tw-mb-2" id="register-modal-title">
                {captionText}
              </div>

              <div className="tw-flex tw-flex-col tw-gap-3 tw-max-w-[250px] tw-ml-2">
                <input
                  type="text"
                  placeholder="Användarnamn"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Lösenord (minst 8 tecken)"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Bekräfta lösenord"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />

                {/* Rollväljare */}
                <div>
                  <label htmlFor="role" className="tw-block tw-text-sm tw-font-medium tw-mb-1">
                    Användartyp:
                  </label>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="tw-w-[205px] tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                  >
                    <option value="Admin">Administratör</option>
                    <option value="Standard">Standard</option>
                  </select>
                  <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-h-[80px]">
                    {selectedRole === 'Admin'
                      ? 'Kan importera data, exportera rapporter \n och skapa schema'
                      : 'Kan endast se scheman och välja station'}
                  </p>
                </div>

                {/* Hemmastation - obligatorisk för Admin */}
                {selectedRole === 'Admin' && (
                  <div>
                    <label htmlFor="homeStation" className="tw-block tw-text-sm tw-font-medium tw-mb-1">
                      Hemmastation:
                    </label>
                    <select
                      id="homeStation"
                      value={homeStation}
                      onChange={(e) => setHomeStation(e.target.value)}
                      className="tw-w-[205px] tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                    >
                      <option value="">Välj station</option>
                      {stationOptions.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>


              <div className="tw-flex tw-justify-start tw-gap-2 tw-mb-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-gray-600 tw-text-white hover:tw-bg-gray-700"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={anyDisabling()}
                  className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-white 
                    ${anyDisabling() ? 'tw-bg-gray-400 tw-cursor-not-allowed' : 'tw-bg-green-600 hover:tw-bg-green-700'}`}
                >
                  Registrera
                </button>
              </div>

              <div className="tw-flex tw-justify-center tw-min-h-[40px]">
                {loading && <BattenburgLoader width={100} left='52%' />}
              </div>
            </div>
          </form>
        </div>

        {/* Höger sida: StationSelector (2/3) */}
        {showStationSelector && newUserId && (
          <div className="tw-w-4/5">
            <StationSelector
              userId={newUserId}
              initialStationName={selectedRole === 'Admin' ? homeStation : undefined}
              onClose={() => {
                setShowStationSelector(false);
                handleClose()
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RegisterForm;
